import type { Job, NurseFullRole } from "../../../utils/models";
import { compareValues, sortByLocationOrder } from "../../../utils/multiColumnSorting";
import { formatCount } from "../WeeklyGapView/CountBadge";
import type { RoleSection } from "../WeeklyGapView/useWeeklyGapView";
import {
	buildGapsDayModel,
	type GapsLocationEntry,
	type GapsShiftGroup,
	type GapsStaffEntry,
	groupKeyOf,
	type KeyedRoleSection,
	shortfallOf,
} from "./gapsDayModel";

// What the gaps-first home's action sheets read off a group. Every number here was already computed by the day
// model (and, under it, by `useWeeklyGapView`); these helpers only choose, order and label it for a sheet.

/**
 * The "N short · M posted" line offered after someone is removed from a group (P3-4).
 *
 * Both numbers are the group's own, read as they stand once the removal has landed in local state — the model is
 * rebuilt from the same data, so no arithmetic here anticipates the removal. They are printed side by side and
 * deliberately NOT netted: a posted request is an ask, not a person, so "2 short · 1 posted" is still two short.
 * Whether `short` draws as short is `isShownAsShort`'s call, for the float-drift reason given there.
 */
export type GapsCoverPrompt = { short: number; posted: number };

export const coverPromptFor = (group: Pick<GapsShiftGroup, "actual" | "required" | "openRequestCount">): GapsCoverPrompt => ({
	short: shortfallOf(group),
	posted: group.openRequestCount,
});

/**
 * Units ordered for the assign-unit and change-location sheets: emptiest first.
 *
 * "Emptiest" is precise: the most people still missing, `required − filled`, largest first — compared on the value
 * as it PRINTS (`formatCount`), so two units that both read `1/1` tie even when float drift left one of them 1e-16
 * short (see `isShownAsShort`). The difference is signed, not clamped: a unit already over its par sorts after one
 * at par, because placing someone there makes it more over. Ties keep the department's location order through
 * the app's location comparator (`compareValues` with the manual order), so they read the way every other location
 * list in the app does.
 */
export const orderLocationsEmptiestFirst = <T extends Pick<GapsLocationEntry, "locationName" | "filled" | "required">>(
	locations: readonly T[],
	customLocationOrder: Map<string, number> | null,
): T[] => {
	const missingAsPrinted = (entry: T): number => Number(formatCount(entry.required - entry.filled));
	return [...locations].sort((a, b) => missingAsPrinted(b) - missingAsPrinted(a) || compareValues(a.locationName, b.locationName, customLocationOrder));
};

/**
 * The group a shift is placed by, for the assign-unit sheet: the window it carries the most weight in.
 *
 * A shift spanning two windows is listed under both (see `distinctStaffEntries`), so "the first group listing it"
 * would place a 7a-7p shift by whichever window happens to sort first. Weight is how much of the window the shift
 * works; a tie goes to the earlier group. `undefined` when no group lists the shift — it is not on this day.
 */
export const findGroupForShift = <G extends GapsShiftGroup>(groups: readonly G[], shiftId: string): G | undefined => {
	let best: { group: G; weight: number } | undefined;
	for (const group of groups) {
		const entry = group.staff.find((candidate) => candidate.slot.nurseInShift.id === shiftId);
		if (entry !== undefined && (best === undefined || entry.slot.weight > best.weight)) best = { group, weight: entry.slot.weight };
	}
	return best?.group;
};

/** Where a shift sits on the day: the group it is placed by and its own entry in that group. */
export type GapsStaffPlacement<G extends GapsShiftGroup> = { group: G; entry: GapsStaffEntry };

/**
 * The group and entry a staff sheet acts on, for a shift id — `findGroupForShift`'s group, and the shift's entry in
 * it (its unit, its hours, whether it counts). `undefined` once the shift is gone from the day, which is how a sheet
 * learns its person was removed.
 */
export const findStaffPlacement = <G extends GapsShiftGroup>(groups: readonly G[], shiftId: string): GapsStaffPlacement<G> | undefined => {
	const group = findGroupForShift(groups, shiftId);
	const entry = group?.staff.find((candidate) => candidate.slot.nurseInShift.id === shiftId);
	return group === undefined || entry === undefined ? undefined : { group, entry };
};

/**
 * The group a sheet should describe after it changed something, by key: the home's own (narrowed) group when the
 * saved scope lists it — the header the user returns to, so the two agree — else the building-wide one, for a
 * change made outside the scope (a call-in logged for someone the filter hides).
 */
export const findGroupByKey = <G extends GapsShiftGroup>(narrowed: readonly G[], dayWide: readonly G[], key: string): G | undefined =>
	// DEFAULT: the building-wide group stands in only when the scope hides it — the same group, its unnarrowed counts.
	narrowed.find((group) => group.key === key) ?? dayWide.find((group) => group.key === key);

/**
 * The department's other units, for Change location's second list (P3-5): every named location the desktop editor
 * offers (`getUniqueLocationNames`), less the group's own, in the app's location order (`sortByLocationOrder`, the
 * manual order when set) — the tie order of the emptiest-first list above it, since these units have no counts to rank.
 * A nameless location is the config's "no unit" — picking it would UNASSIGN the person, which is not a change of
 * location — so it is never offered here. (A post is different: it lists the classic post screen's units, where a
 * blank one reads as "any unit".)
 */
export const otherUnitsForChange = (
	departmentLocationNames: readonly string[],
	groupUnits: readonly Pick<GapsLocationEntry, "locationName">[],
	customLocationOrder: Map<string, number> | null,
): string[] => {
	const onShift = new Set(groupUnits.map((unit) => unit.locationName));
	return sortByLocationOrder(
		departmentLocationNames.filter((locationName) => locationName !== "" && !onShift.has(locationName)),
		customLocationOrder,
	);
};

/**
 * What the add and post sheets need to scope themselves to a group's section.
 *
 * `roles` is `resolveSectionRoles`'s answer — the roles the desktop grid hands its own add dialog for the section:
 * the role itself, a role group's members, and `[]` for a job section, which is scoped by `jobId` instead.
 */
export type GapsSectionScope = {
	roles: NurseFullRole[];
	/** The job a job section stands for; `null` for a role or role-group section, and for an ambiguous job section. */
	jobId: string | null;
	/**
	 * A job section whose display name two or more ENABLED jobs share: their ids, in the enabled-job order. The section
	 * is keyed by the name alone, so it cannot say which of them it is — nothing may be posted or added under it
	 * (`isSectionWriteBlocked`), since picking one would post, add and count people under a job the section may not be.
	 * `null` for every section that names one job, or none.
	 */
	ambiguousJobIds: string[] | null;
};

/** The job a section stands for, as `resolveSectionJob` settles it. */
export type GapsSectionJob = Pick<GapsSectionScope, "jobId" | "ambiguousJobIds">;

export type GapsScopedShiftGroup = GapsShiftGroup & { scope: GapsSectionScope };

/**
 * The job a job section was built for.
 *
 * `useWeeklyGapView` names a job section `job.displayName`, iterating `useJobs()` — the department's ENABLED jobs —
 * so that is the list to search, with the same key. `chosenDepartment.jobs` also holds jobs that are no longer
 * enabled, and one of those sharing a display name would be found first. The lookup cannot miss in practice: both
 * lists come from the same department config in the same render. If it ever does, it throws rather than answer
 * `null`, which would open the sheets unscoped and look like a building-wide post.
 *
 * Two ENABLED jobs sharing the display name is a real (if unusual) configuration, not a broken render, so it is
 * answered rather than thrown: the section is marked ambiguous (`ambiguousJobIds`) with no job, the home keeps
 * working, and only that section's post and add are refused, with the reason on screen (`GapsShiftGroup`). The
 * error is reported by `useReportAmbiguousJobSections`, not here — this is a pure function run during render.
 */
export const resolveSectionJob = (section: RoleSection, enabledJobs: readonly Job[]): GapsSectionJob => {
	if (section.isJobSection !== true) return { jobId: null, ambiguousJobIds: null };
	const named = enabledJobs.filter((candidate) => candidate.displayName === section.shortRole);
	if (named.length === 0) throw new Error(`Weekly gap view built a job section "${section.shortRole}" that no enabled job is named`);
	if (named.length > 1) return { jobId: null, ambiguousJobIds: named.map((job) => job.id) };
	return { jobId: named[0].id, ambiguousJobIds: null };
};

/**
 * Whether nothing may be posted or added under a group's section: its job cannot be told apart from another enabled
 * job of the same name (see `GapsSectionScope.ambiguousJobIds`).
 */
export const isSectionWriteBlocked = (group: Pick<GapsScopedShiftGroup, "scope">): boolean => group.scope.ambiguousJobIds !== null;

/**
 * `buildGapsDayModel`, with each group carrying its section's scope, and keyed by its SECTION (`keySections`) — the
 * day model keys a group by its label, and two sections can share one (C5): the key is what React, the open-group map,
 * the sheets' intents and `findGroupByKey` tell groups apart by.
 *
 * The scope is resolved by the caller because the desktop's role resolver (`resolveSectionRoles`) lives beside the
 * grid's row components in `FlatRowRenderer.tsx`: importing it here would pull those components, and their contexts,
 * into every consumer of this pure module. It is resolved once per section, not once per group.
 */
export const buildScopedGapsDayModel = (
	sections: KeyedRoleSection[],
	dayIndex: number,
	scopeForSection: (section: RoleSection) => GapsSectionScope,
): GapsScopedShiftGroup[] =>
	sections.flatMap((section) => {
		const scope = scopeForSection(section);
		return buildGapsDayModel([section], dayIndex).map((group) => ({ ...group, key: groupKeyOf(section.sectionKey, group.timeLabel), scope }));
	});
